import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Settings, LogOut, User, Shield, ChevronDown, DollarSign, CreditCard } from 'lucide-react';

export const UserMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 hover:bg-[#1a1a1a] transition-colors rounded"
        data-testid="user-menu-btn"
      >
        <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center overflow-hidden border border-[#333]">
          {user.profile_picture ? (
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/picture/${user.profile_picture}`}
              alt={user.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <User size={16} className="text-[#888888]" />
          )}
        </div>
        <span className="text-white font-mono text-sm hidden md:block">{user.username}</span>
        <ChevronDown size={14} className={`text-[#888888] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-[#0a0a0a] border border-[#1a1a1a] shadow-xl z-50" data-testid="user-menu-dropdown">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-[#1a1a1a]">
            <p className="text-white font-['Chakra_Petch'] uppercase text-sm">{user.username}</p>
            <p className="text-[#888888] font-mono text-xs">{user.email}</p>
            {user.is_artist && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-[#00CCFF] text-black text-[10px] font-mono uppercase">
                Artist
              </span>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => { navigate('/profile'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-[#888888] hover:text-white hover:bg-[#1a1a1a] transition-colors"
              data-testid="menu-profile"
            >
              <User size={16} />
              <span className="font-mono text-sm">My Profile</span>
            </button>

            <button
              onClick={() => { navigate('/settings'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-[#888888] hover:text-white hover:bg-[#1a1a1a] transition-colors"
              data-testid="menu-settings"
            >
              <Settings size={16} />
              <span className="font-mono text-sm">Account Settings</span>
            </button>

            {/* iter_487 — Unified My Subscriptions */}
            <button
              onClick={() => { navigate('/my-subscriptions'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-[#888888] hover:text-white hover:bg-[#1a1a1a] transition-colors"
              data-testid="menu-my-subscriptions"
            >
              <CreditCard size={16} className="text-[#FFDA33]" />
              <span className="font-mono text-sm">My Subscriptions</span>
            </button>

            <button
              onClick={() => { navigate('/paid-to-listen'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-[#888888] hover:text-white hover:bg-[#1a1a1a] transition-colors"
              data-testid="menu-paid-to-listen"
            >
              <DollarSign size={16} className="text-[#00ff88]" />
              <span className="font-mono text-sm">Paid To Listen</span>
            </button>

            {user.is_artist && (
              <button
                onClick={() => { navigate('/dashboard'); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-[#888888] hover:text-white hover:bg-[#1a1a1a] transition-colors"
                data-testid="menu-dashboard"
              >
                <Shield size={16} />
                <span className="font-mono text-sm">Artist Dashboard</span>
              </button>
            )}
          </div>

          {/* Logout */}
          <div className="border-t border-[#1a1a1a] py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-red-500 hover:text-red-400 hover:bg-[#1a1a1a] transition-colors"
              data-testid="menu-logout"
            >
              <LogOut size={16} />
              <span className="font-mono text-sm">Log Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
